import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import iconNote from '@/assets/print/icon-note.svg'
import iconUser from '@/assets/print/icon-user.svg'
import iconCalendar from '@/assets/print/icon-calander.svg'

interface WrongNotePrintHeaderProps {
  /** 헤더 제목. 생략 시 "오답노트". */
  title?: string
  lessonTitle?: string
  lessonNumber?: number
  sourceScope?: string
  learnerName: string
  /** "2025.05.16 14:35" 형태의 인쇄/생성 일시 텍스트 */
  dateText: string
}

/**
 * 오답노트 인쇄물 첫 페이지 상단 헤더 (Figma node 654:942).
 * 좌측: icon-note + 제목 / 우측: 이름·날짜 / 하단: 구분선.
 * 2페이지부터는 렌더하지 않는다(첫 페이지 전용).
 */
export function WrongNotePrintHeader({ title, learnerName, dateText, lessonTitle, lessonNumber, sourceScope }: WrongNotePrintHeaderProps) {
  const { t } = useTranslation('coaching')
  const headerTitle = title ?? t('print.wrongNoteTitle')

  if (lessonTitle) {
    return (
      <div className="px-[72px] pt-[48px]">
        <div className="flex min-h-[26px] items-start justify-between gap-[24px]">
          <span className="shrink-0 rounded-full bg-[#edf5ff] px-[8px] py-[4px] text-[14px] font-semibold leading-[17px] text-[#4599ff]">{headerTitle}</span>
          <div className="flex items-center gap-[24px] leading-[26px]">
            <HeaderField icon={iconUser} label={t('print.learnerLabel')} value={learnerName} />
            <HeaderField icon={iconCalendar} label={t('print.dateLabel')} value={dateText} />
          </div>
        </div>
        <div
          className="mt-[4px] flex items-baseline gap-[8px] p-[8px] text-[28px] font-bold leading-[34px] tracking-[-0.168px] text-lc-gray-900"
          style={{ fontFamily: 'var(--font-dunggeunmiso)' }}
        >
          {lessonNumber != null && <span className="shrink-0 text-[#4599ff]">{String(lessonNumber).padStart(2, '0')}.</span>}
          <span className="min-w-0 [overflow-wrap:anywhere]"><ContentMetadataText text={lessonTitle} /></span>
        </div>
        {sourceScope ? <p className="mt-[8px] text-[13px] leading-[20px] text-lc-gray-900 [overflow-wrap:anywhere]">{sourceScope}</p> : null}
        <div className="mt-[14px] h-[2px] w-full bg-[#e5e5e5]" />
      </div>
    )
  }

  return (
    <div className="px-[72px] pt-[56px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[12px]">
          <img src={iconNote} width={40} height={40} alt="" aria-hidden />
          <span
            className="font-dunggeunmiso text-[28px] font-bold leading-[1.2] tracking-[-0.168px] text-lc-gray-900"
            style={{ fontFamily: 'var(--font-dunggeunmiso)' }}
          >
            {headerTitle}
          </span>
        </div>
        <div className="flex items-center gap-[24px]">
          <HeaderField icon={iconUser} label={t('print.learnerLabel')} value={learnerName} />
          <HeaderField icon={iconCalendar} label={t('print.dateLabel')} value={dateText} />
        </div>
      </div>
      <div className="mt-[30px] h-px w-full bg-[#e5e5e5]" />
    </div>
  )
}

function HeaderField({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-[8px]">
      <img src={icon} width={18} height={18} alt="" aria-hidden />
      <span className="text-[13px] font-medium tracking-[-0.078px] text-[#b2b2b2]">{label}</span>
      {value ? (
        <span className="text-[15px] font-medium tracking-[-0.09px] text-lc-gray-900">{value}</span>
      ) : (
        // 원생 없이 출력한 시험지: 이름을 손으로 적을 빈칸
        <span data-testid="print-header-blank-field" className="inline-block h-[20px] w-[96px] border-b border-[#b2b2b2]" />
      )}
    </div>
  )
}
